<!-- NOTICE: KAPTION-INTEGRATION -->
<!-- Schedule Message Popover - Allows scheduling messages for future delivery -->
<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import {
  addMinutes,
  addHours,
  addDays,
  format,
  isBefore,
  startOfMinute,
} from 'date-fns';
import {
  es as esLocale,
  pt as ptLocale,
  fr as frLocale,
  de as deLocale,
  it as itLocale,
  tr as trLocale,
  enUS as enLocale,
} from 'date-fns/locale';
import * as chrono from 'chrono-node';
import Button from 'dashboard/components-next/button/Button.vue';
import KaptionSendIcon from 'dashboard/components/icons/KaptionSendIcon.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'schedule']);

const { locale, t } = useI18n();

// State
const inputValue = ref('');
const parsedDate = ref(null);
const selectedTimezone = ref('');
const errorMessage = ref('');
const isSubmitting = ref(false);
const showTimezoneDropdown = ref(false);
const showQuickOptionsDropdown = ref(false);
const timezoneSearch = ref('');
const highlightedIndex = ref(-1);

// Common timezones for quick access
const COMMON_TIMEZONES = [
  {
    value: 'America/New_York',
    label: 'New York (EST/EDT)',
    region: 'Americas',
  },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)', region: 'Americas' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)', region: 'Americas' },
  {
    value: 'America/Los_Angeles',
    label: 'Los Angeles (PST/PDT)',
    region: 'Americas',
  },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)', region: 'Americas' },
  {
    value: 'America/Buenos_Aires',
    label: 'Buenos Aires (ART)',
    region: 'Americas',
  },
  {
    value: 'America/Mexico_City',
    label: 'Mexico City (CST/CDT)',
    region: 'Americas',
  },
  { value: 'America/Bogota', label: 'Bogota (COT)', region: 'Americas' },
  { value: 'America/Lima', label: 'Lima (PET)', region: 'Americas' },
  { value: 'America/Santiago', label: 'Santiago (CLT)', region: 'Americas' },
  { value: 'Europe/London', label: 'London (GMT/BST)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)', region: 'Europe' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', region: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', region: 'Asia' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)', region: 'Asia' },
  { value: 'Asia/Kolkata', label: 'Mumbai (IST)', region: 'Asia' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)', region: 'Pacific' },
  {
    value: 'Pacific/Auckland',
    label: 'Auckland (NZST/NZDT)',
    region: 'Pacific',
  },
  { value: 'UTC', label: 'UTC', region: 'UTC' },
];

// Get UTC offset for a timezone (simplified format like "GMT-3")
const getTimezoneOffset = timezone => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find(p => p.type === 'timeZoneName');
    return offsetPart ? offsetPart.value : 'UTC';
  } catch {
    return 'UTC';
  }
};

// Simplified timezone display (just GMT offset)
const simplifiedTimezone = computed(() => {
  return getTimezoneOffset(selectedTimezone.value);
});

// Filtered timezones based on search
const filteredTimezones = computed(() => {
  if (!timezoneSearch.value) {
    return COMMON_TIMEZONES;
  }
  const query = timezoneSearch.value.toLowerCase();
  return COMMON_TIMEZONES.filter(
    tz =>
      tz.label.toLowerCase().includes(query) ||
      tz.value.toLowerCase().includes(query) ||
      tz.region.toLowerCase().includes(query)
  );
});

// Select timezone from dropdown
const selectTimezoneOption = tz => {
  selectedTimezone.value = tz.value;
  showTimezoneDropdown.value = false;
  timezoneSearch.value = '';
};

// Detect user's timezone on mount
onMounted(() => {
  try {
    selectedTimezone.value = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    selectedTimezone.value = 'UTC';
  }
});

// Get date-fns locale based on current i18n locale
const getDateFnsLocale = () => {
  const lang = locale.value.split('-')[0];
  const locales = {
    en: enLocale,
    es: esLocale,
    pt: ptLocale,
    fr: frLocale,
    de: deLocale,
    it: itLocale,
    tr: trLocale,
  };
  return locales[lang] || enLocale;
};

// Get the appropriate chrono parser for the current locale
const getChronoParser = () => {
  const lang = locale.value.split('-')[0];
  const parsers = {
    en: chrono.en,
    es: chrono.es,
    pt: chrono.pt,
    fr: chrono.fr,
    de: chrono.de,
    nl: chrono.nl,
    ja: chrono.ja,
    ru: chrono.ru,
    uk: chrono.uk,
  };
  return parsers[lang] || chrono.en;
};

// Quick time suggestions - ordered logically by time proximity
const quickSuggestions = computed(() => [
  {
    key: 'in_15_min',
    label: t('CONVERSATION.SCHEDULE_MESSAGE.IN_15_MINUTES'),
    getDate: () => addMinutes(new Date(), 15),
  },
  {
    key: 'in_1_hour',
    label: t('CONVERSATION.SCHEDULE_MESSAGE.IN_1_HOUR'),
    getDate: () => addHours(new Date(), 1),
  },
  {
    key: 'in_2_hours',
    label: t('CONVERSATION.SCHEDULE_MESSAGE.IN_2_HOURS'),
    getDate: () => addHours(new Date(), 2),
  },
  {
    key: 'tomorrow_9am',
    label: t('CONVERSATION.SCHEDULE_MESSAGE.TOMORROW_9AM'),
    getDate: () => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      return tomorrow;
    },
  },
  {
    key: 'tomorrow_2pm',
    label: t('CONVERSATION.SCHEDULE_MESSAGE.TOMORROW_2PM'),
    getDate: () => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(14, 0, 0, 0);
      return tomorrow;
    },
  },
  {
    key: 'in_3_days',
    label: t('CONVERSATION.SCHEDULE_MESSAGE.IN_3_DAYS'),
    getDate: () => addDays(new Date(), 3),
  },
  {
    key: 'next_monday_9am',
    label: t('CONVERSATION.SCHEDULE_MESSAGE.NEXT_MONDAY_9AM'),
    getDate: () => {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
      const nextMonday = new Date();
      nextMonday.setDate(today.getDate() + daysUntilMonday);
      nextMonday.setHours(9, 0, 0, 0);
      return nextMonday;
    },
  },
]);

// Format date using locale-aware formatting
const formatDateLocalized = date => {
  const dateLocale = getDateFnsLocale();
  return format(date, 'PPPp', { locale: dateLocale });
};

// Formatted preview of selected date
const formattedPreview = computed(() => {
  if (!parsedDate.value) return '';
  const dateLocale = getDateFnsLocale();
  return format(parsedDate.value, 'PPPP p', { locale: dateLocale });
});

// Check if we can schedule
const canSchedule = computed(() => {
  return (
    parsedDate.value &&
    !errorMessage.value &&
    selectedTimezone.value &&
    !isSubmitting.value
  );
});

// Parse natural language input
const parseNaturalLanguage = text => {
  if (!text || text.trim() === '') {
    return null;
  }

  const trimmedText = text.trim().toLowerCase();

  // Handle "now" explicitly
  const nowKeywords = [
    'now',
    'ahora',
    'agora',
    'maintenant',
    'jetzt',
    'nu',
    '今',
  ];
  if (nowKeywords.includes(trimmedText)) {
    return addMinutes(new Date(), 1); // Schedule at least 1 minute in the future
  }

  // Get the appropriate parser for the current locale
  const parser = getChronoParser();

  // Use chrono to parse natural language with locale support
  const results = parser.parse(trimmedText, new Date(), { forwardDate: true });

  if (results.length > 0) {
    return results[0].start.date();
  }

  return null;
};

// Handle input change
const handleInput = () => {
  errorMessage.value = '';
  const parsed = parseNaturalLanguage(inputValue.value);

  if (parsed) {
    // Check if date is in the past
    const now = startOfMinute(new Date());
    if (isBefore(parsed, now)) {
      errorMessage.value = t(
        'CONVERSATION.SCHEDULE_MESSAGE.PAST_DATE_ERROR',
        'Cannot schedule for a time in the past'
      );
      parsedDate.value = null;
      return;
    }

    parsedDate.value = parsed;
  } else if (inputValue.value.trim() !== '') {
    errorMessage.value = t(
      'CONVERSATION.SCHEDULE_MESSAGE.INVALID_DATE_ERROR',
      'Could not parse date. Try "tomorrow at 2pm" or "in 1 hour"'
    );
    parsedDate.value = null;
  } else {
    parsedDate.value = null;
  }
};

// Handle quick suggestion click
const selectSuggestion = suggestion => {
  const date = suggestion.getDate();

  // Check if date is in the past
  const now = startOfMinute(new Date());
  if (isBefore(date, now)) {
    errorMessage.value = t(
      'CONVERSATION.SCHEDULE_MESSAGE.PAST_DATE_ERROR',
      'Cannot schedule for a time in the past'
    );
    parsedDate.value = null;
    return;
  }

  // Set the formatted date as input value
  inputValue.value = formatDateLocalized(date);
  parsedDate.value = date;
  errorMessage.value = '';
  highlightedIndex.value = -1;
};

// Handle schedule button click
const handleSchedule = async () => {
  if (!canSchedule.value) return;

  isSubmitting.value = true;

  try {
    emit('schedule', {
      scheduledAt: parsedDate.value.toISOString(),
      timezone: selectedTimezone.value,
    });
  } finally {
    isSubmitting.value = false;
  }
};

// Handle keyboard navigation for quick options
const handleKeydown = event => {
  if (!showQuickOptionsDropdown.value) {
    // Open dropdown on arrow keys if closed
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      showQuickOptionsDropdown.value = true;
      highlightedIndex.value = 0;
    }
    return;
  }

  const suggestions = quickSuggestions.value;
  const maxIndex = suggestions.length - 1;

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      event.stopPropagation();
      if (highlightedIndex.value < maxIndex) {
        highlightedIndex.value += 1;
      } else {
        highlightedIndex.value = 0;
      }
      break;
    case 'ArrowUp':
      event.preventDefault();
      event.stopPropagation();
      if (highlightedIndex.value > 0) {
        highlightedIndex.value -= 1;
      } else {
        highlightedIndex.value = maxIndex;
      }
      break;
    case 'Enter':
      event.preventDefault();
      event.stopPropagation();
      if (highlightedIndex.value >= 0 && highlightedIndex.value <= maxIndex) {
        selectSuggestion(suggestions[highlightedIndex.value]);
        showQuickOptionsDropdown.value = false;
      }
      break;
    case 'Escape':
      event.preventDefault();
      event.stopPropagation();
      showQuickOptionsDropdown.value = false;
      highlightedIndex.value = -1;
      break;
    default:
      // For any other key, keep dropdown open while typing
      break;
  }
};

// Handle close
const handleClose = () => {
  emit('close');
};

// Handle click outside
const handleClickOutside = () => {
  if (props.show) {
    handleClose();
  }
};

// Close quick options when clicking outside
const closeQuickOptions = () => {
  showQuickOptionsDropdown.value = false;
  highlightedIndex.value = -1;
};

// Reset state when popover opens
watch(
  () => props.show,
  newValue => {
    if (newValue) {
      inputValue.value = '';
      parsedDate.value = null;
      errorMessage.value = '';
      isSubmitting.value = false;
      showTimezoneDropdown.value = false;
      showQuickOptionsDropdown.value = false;
      highlightedIndex.value = -1;
      timezoneSearch.value = '';
    }
  }
);
</script>

<template>
  <OnClickOutside @trigger="handleClickOutside">
    <div
      v-if="show"
      class="absolute bottom-full right-0 mb-2 w-[520px] bg-n-solid-2 border border-n-container rounded-xl shadow-lg z-50"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-4 py-3 border-b border-n-container"
      >
        <div class="flex items-center gap-2">
          <span class="i-lucide-clock text-n-slate-11 w-4 h-4" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CONVERSATION.SCHEDULE_MESSAGE.TITLE', 'Schedule Message') }}
          </span>
        </div>
        <button
          type="button"
          class="text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="handleClose"
        >
          <span class="i-lucide-x w-4 h-4" />
        </button>
      </div>

      <!-- Content -->
      <div class="p-4 space-y-3">
        <!-- When to send label -->
        <label class="text-xs font-medium text-n-slate-11 block">
          {{ t('CONVERSATION.SCHEDULE_MESSAGE.WHEN_TO_SEND', 'When to send') }}
        </label>

        <!-- Input row: text input + timezone button -->
        <div class="flex gap-2">
          <!-- Text input with suggestions dropdown (like NaturalLanguageDateTimePicker) -->
          <div class="relative flex-1">
            <input
              v-model="inputValue"
              type="text"
              :placeholder="
                t(
                  'CONVERSATION.SCHEDULE_MESSAGE.PLACEHOLDER',
                  'Type or select...'
                )
              "
              class="w-full px-3 py-2 text-sm border rounded-lg transition-colors bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-opacity-20"
              :class="[
                errorMessage
                  ? 'border-n-ruby-7 focus:border-n-ruby-8 focus:ring-n-ruby-8'
                  : parsedDate
                    ? 'border-n-green-7 focus:border-n-green-8 focus:ring-n-green-8'
                    : 'border-n-slate-6 focus:border-n-brand focus:ring-n-brand',
              ]"
              @input="handleInput"
              @focus="
                showQuickOptionsDropdown = true;
                showTimezoneDropdown = false;
                highlightedIndex = 0;
              "
              @blur="setTimeout(() => closeQuickOptions(), 150)"
              @keydown="handleKeydown"
            />

            <!-- Success/Error Icon -->
            <div
              v-if="parsedDate || errorMessage"
              class="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <span
                v-if="parsedDate"
                class="i-lucide-check text-n-green-11 w-4 h-4"
              />
              <span
                v-else-if="errorMessage"
                class="i-lucide-alert-circle text-n-ruby-11 w-4 h-4"
              />
            </div>

            <!-- Quick Options Dropdown (opens upward, like NaturalLanguageDateTimePicker) -->
            <div
              v-if="showQuickOptionsDropdown"
              class="absolute bottom-full left-0 right-0 mb-1 bg-n-solid-2 border border-n-container rounded-lg shadow-lg z-[60] max-h-64 overflow-y-auto"
            >
              <div class="p-2">
                <div class="text-xs font-medium text-n-slate-11 px-2 py-1">
                  {{ t('CONVERSATION.SCHEDULE_MESSAGE.QUICK_OPTIONS') }}
                </div>
                <button
                  v-for="(suggestion, index) in quickSuggestions"
                  :key="suggestion.key"
                  type="button"
                  class="w-full text-left px-2 py-2 text-sm rounded transition-colors text-n-slate-12"
                  :class="[
                    index === highlightedIndex
                      ? 'bg-n-alpha-black3'
                      : 'hover:bg-n-alpha-black2',
                  ]"
                  @mousedown.prevent="
                    selectSuggestion(suggestion);
                    showQuickOptionsDropdown = false;
                  "
                  @mouseenter="highlightedIndex = index"
                >
                  {{ suggestion.label }}
                </button>
              </div>
            </div>
          </div>

          <!-- Timezone Button -->
          <div class="relative flex-shrink-0">
            <button
              type="button"
              class="flex items-center gap-1 px-2.5 py-2 text-sm border rounded-lg transition-colors bg-n-alpha-black2 text-n-slate-12 border-n-slate-6 hover:border-n-slate-7 focus:outline-none focus:ring-2 focus:ring-n-brand focus:ring-opacity-20"
              @click="
                showTimezoneDropdown = !showTimezoneDropdown;
                showQuickOptionsDropdown = false;
              "
            >
              <span class="font-mono text-xs">{{ simplifiedTimezone }}</span>
              <span
                class="i-lucide-chevron-down text-n-slate-11 w-3 h-3 transition-transform"
                :class="{ 'rotate-180': showTimezoneDropdown }"
              />
            </button>

            <!-- Timezone Dropdown (opens upward) -->
            <div
              v-if="showTimezoneDropdown"
              class="absolute bottom-full right-0 mb-1 w-64 bg-n-solid-2 border border-n-container rounded-lg shadow-lg z-[60] max-h-56 overflow-hidden"
            >
              <div class="p-2 border-b border-n-container">
                <div class="relative">
                  <span
                    class="absolute left-2.5 top-1/2 -translate-y-1/2 i-lucide-search text-n-slate-10 w-3.5 h-3.5"
                  />
                  <input
                    v-model="timezoneSearch"
                    type="text"
                    :placeholder="
                      t(
                        'CONVERSATION.SCHEDULE_MESSAGE.SEARCH_TIMEZONE',
                        'Search...'
                      )
                    "
                    class="w-full pl-8 pr-3 py-1.5 text-xs border rounded-md bg-n-alpha-black2 text-n-slate-12 border-n-slate-6 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
                  />
                </div>
              </div>
              <div class="max-h-40 overflow-y-auto">
                <button
                  v-for="tz in filteredTimezones"
                  :key="tz.value"
                  type="button"
                  class="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-left hover:bg-n-alpha-black2 transition-colors"
                  :class="{
                    'bg-n-alpha-black2': tz.value === selectedTimezone,
                  }"
                  @click="selectTimezoneOption(tz)"
                >
                  <span class="text-n-slate-12 truncate">{{ tz.label }}</span>
                  <div class="flex items-center gap-1.5 flex-shrink-0">
                    <span class="text-n-slate-10 font-mono">{{
                      getTimezoneOffset(tz.value)
                    }}</span>
                    <span
                      v-if="tz.value === selectedTimezone"
                      class="i-lucide-check text-n-green-11 w-3.5 h-3.5"
                    />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="text-xs text-n-ruby-11">
          {{ errorMessage }}
        </div>

        <!-- Preview (when date is selected) -->
        <div v-else-if="formattedPreview" class="text-xs text-n-green-11">
          {{
            t('CONVERSATION.SCHEDULE_MESSAGE.SCHEDULED_FOR', {
              date: formattedPreview,
            })
          }}
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-2 px-4 py-3 border-t border-n-container bg-n-alpha-black1"
      >
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          :disabled="isSubmitting"
          @click="handleClose"
        >
          {{ t('CONVERSATION.SCHEDULE_MESSAGE.CANCEL', 'Cancel') }}
        </Button>
        <Button
          variant="solid"
          color="blue"
          size="sm"
          :disabled="!canSchedule"
          :is-loading="isSubmitting"
          @click="handleSchedule"
        >
          <template #icon>
            <KaptionSendIcon :size="16" />
          </template>
          {{ t('CONVERSATION.SCHEDULE_MESSAGE.SCHEDULE', 'Schedule') }}
        </Button>
      </div>
    </div>
  </OnClickOutside>
</template>
